import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Quote, Star } from "lucide-react";

const testimonials = [
  {
    quote: "Die Firma Freitag hat unser gesamtes Bauunternehmen vom Anfang bis zum Ende begleitet. Wir waren und sind sehr zufrieden. Das Preis-Leistungsverhältnis ist gut, Termine werden exakt eingehalten. Wir würden die Firma Freitag immer wieder beauftragen und können diese ohne Einschränkung weiterempfehlen.",
    author: "Matthias Lücke",
    company: "Google Bewertung",
    role: "Kunde",
    color: "blue",
    rating: 5,
    project: "Bauunternehmung",
  },
  {
    quote: "Wir haben ein Haus erworben, bei dem die Terrasse und der Weg komplett neu gepflastert werden mussten. Das Unternehmen ist bekannt durch seine günstigen Preise und dennoch qualitativ guter und sauberer Arbeit – und das können wir auch wirklich genauso bestätigen. Wir können die Firma Freitag wirklich JEDEM empfehlen!",
    author: "Florian Fricke",
    company: "Google Bewertung",
    role: "Kunde",
    color: "green",
    rating: 5,
    project: "Pflasterarbeiten",
  },
  {
    quote: "Ich brauchte einen Gutachter für einen Feuchte-Schaden. Herr Freitag ist schnell und pünktlich erschienen. Das Gutachten hatte ich sehr schnell von ihm erhalten. Ich bin sehr zufrieden.",
    author: "Michael Bertram",
    company: "Google Bewertung",
    role: "Kunde",
    color: "orange",
    rating: 5,
    project: "Gutachten",
  },
  {
    quote: "Einfach nur top. Firma Freitag hat sich um unsere Badezimmer-Renovierung gekümmert. Es hat alles hervorragend geklappt. Bin sehr zufrieden. Immer wieder gerne.",
    author: "Carmen Behrens",
    company: "Google Bewertung",
    role: "Kundin",
    color: "blue",
    rating: 5,
    project: "Renovierung",
  },
  {
    quote: "Lief alles super. Revisionsschacht im Vorgarten wurde preisgünstig realisiert. Immer wieder. Beste Grüße",
    author: "Fa Schneider",
    company: "Google Bewertung",
    role: "Kunde",
    color: "green",
    rating: 5,
    project: "Tiefbau",
  },
  {
    quote: "Super nett und hilfsbereit. Schnelle und zuverlässige Abwicklung. Vielen Dank. (Dichtheitsprüfung Abwassertank)",
    author: "Christian Jäschke",
    company: "Google Bewertung",
    role: "Kunde",
    color: "orange",
    rating: 5,
    project: "Dichtheitsprüfung",
  },
];

const colorMap = {
  blue: { border: "border-l-brand-blue", text: "text-brand-blue-light", line: "bg-brand-blue", badge: "bg-brand-blue-muted text-brand-blue-light" },
  green: { border: "border-l-brand-green", text: "text-brand-green-light", line: "bg-brand-green", badge: "bg-brand-green-muted text-brand-green-light" },
  orange: { border: "border-l-brand-orange", text: "text-brand-orange-light", line: "bg-brand-orange", badge: "bg-brand-orange-muted text-brand-orange-light" },
};

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-background" ref={ref}>
      <div className="container-wide">
        {/* Header with rating summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              Kundenstimmen
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
              Was unsere Kunden sagen.
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
              Vertrauen entsteht durch Ergebnisse. Hier berichten unsere Kunden von ihren Erfahrungen mit der Freitag-Gruppe.
            </p>

            {/* Rating summary box */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="font-heading font-bold text-4xl text-foreground">4.9</span>
                <div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 fill-brand-orange text-brand-orange" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground font-body mt-1">Google Bewertungen · Stand Juni 2026</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                <div className="w-8 h-1 gradient-blue" />
                <div className="w-8 h-1 gradient-orange" />
                <div className="w-8 h-1 gradient-green" />
              </div>
            </div>
          </motion.div>

          {/* First two reviews prominently */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            {testimonials.slice(0, 2).map((testimonial, index) => {
              const colors = colorMap[testimonial.color];
              return (
                <motion.div
                  key={testimonial.author}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className={`border-0 border-l-2 ${colors.border} bg-card h-full`}>
                    <CardContent className="p-8 flex flex-col h-full">
                      <div className="flex items-center justify-between mb-4">
                        <span className={`text-[10px] px-3 py-1 font-heading uppercase tracking-wider ${colors.badge}`}>
                          {testimonial.project}
                        </span>
                        <div className="flex gap-0.5">
                          {Array(testimonial.rating).fill(0).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-brand-orange text-brand-orange" />
                          ))}
                        </div>
                      </div>
                      <Quote className="w-6 h-6 text-muted-foreground/20 mb-4" />
                      <p className="text-sm md:text-base text-foreground font-body leading-relaxed mb-6 flex-1">
                        „{testimonial.quote}“
                      </p>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${colors.badge} flex items-center justify-center`}>
                          <span className="font-heading font-bold text-xs">
                            {testimonial.author.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-sm text-foreground">
                            {testimonial.author}
                          </p>
                          <p className="text-xs text-muted-foreground font-body">
                            {testimonial.role} · {testimonial.company}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* More reviews in a grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {testimonials.slice(2).map((testimonial, index) => {
            const colors = colorMap[testimonial.color];
            return (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              >
                <Card className={`border-0 border-l-2 ${colors.border} bg-card h-full`}>
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-3">
                      {Array(testimonial.rating).fill(0).map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-brand-orange text-brand-orange" />
                      ))}
                    </div>
                    <p className="text-xs text-foreground font-body leading-relaxed mb-4 flex-1">
                      „{testimonial.quote}“
                    </p>
                    <div>
                      <p className="font-heading font-semibold text-xs text-foreground">
                        {testimonial.author}
                      </p>
                      <p className="text-[10px] text-muted-foreground font-body">
                        {testimonial.company}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
